import React from 'react';
import styled from 'styled-components';

type TooltipAnchorProps = {
  arrowPosition?: 'bottom' | 'left' | 'right' | 'top' | 'topright' | 'bottom-topright';
  className?: string;
  id: string;
  text?: string;
};

const TooltipWrapper = styled.div`
  display: none;
  transition: opacity 0.2s ease-in-out;
  position: absolute;
  background: #000;
  color: #fff;
  font-size: 14px;
  line-height: 16px;
  border-radius: 4px;
  padding: 6px 7px;
  white-space: normal;
  overflow-wrap: break-word;
  word-break: break-word;
  text-align: start;
  width: max-content;
  max-width: var(--wf-max-width, 260px);
  z-index: 20100;
  box-shadow: 0px 2px 12px rgba(0, 0, 0, 0.2);

  &::before {
    content: '';
    position: absolute;
    width: 0;
    height: 0;
  }

  &[data-arrow-position='left'] {
    transform: translateY(-50%);
    margin: 0 11px;

    &::before {
      left: -9px;
      border-right: 10px solid #000;
    }
  }

  &[data-arrow-position='right'] {
    transform: translate(calc(-100% - 11px), -50%);
    margin: 0 11px 0 0;

    &::before {
      right: -9px;
      border-left: 10px solid #000;
    }
  }

  &[data-arrow-position='left']::before,
  &[data-arrow-position='right']::before {
    top: 5px;
    border-bottom: 9px solid transparent;
    border-top: 9px solid transparent;
  }

  &[data-arrow-position='bottom'] {
    transform: translate(-50%, calc(-100% - 11px));
    margin: 0 0 11px;

    &::before {
      bottom: -9px;
      border-top: 10px solid #000;
    }
  }

  &[data-arrow-position='bottom-topright'] {
    transform: translate(calc(-100% - 18.5px), calc(-100% - 11px));
    margin: 0 0 11px;

    [dir='rtl'] & {
      transform: translate(18.5px, calc(-100% - 11px));
    }

    &::before {
      bottom: -9px;
      inset-inline-end: 10px;
      border-inline-start: 9px solid transparent;
      border-inline-end: 9px solid transparent;
      border-top: 10px solid #000;

      [dir='rtl'] & {
        inset-inline-end: auto;
        inset-inline-start: 10px;
      }
    }
  }

  &[data-arrow-position='top'] {
    transform: translateX(-50%);
    margin: 11px 0;
  }

  &[data-arrow-position='bottom']::before,
  &[data-arrow-position='top']::before {
    inset-inline-start: calc(50% - 9px);
    border-inline-start: 9px solid transparent;
    border-inline-end: 9px solid transparent;
  }

  &[data-arrow-position='topright'] {
    transform: translateX(calc(-100% - 18.5px));
    margin: 11px 0;

    [dir='rtl'] & {
      transform: translateX(18.5px);
    }

    &::before {
      inset-inline-end: 10px;
      border-inline-start: 9px solid transparent;
      border-inline-end: 9px solid transparent;
    }
  }

  &[data-arrow-position='topright']::before,
  &[data-arrow-position='top']::before {
    top: -9px;
    border-bottom: 10px solid #000;
  }
`;

const TooltipAnchor = ({ id, text = '', arrowPosition = 'bottom' }: TooltipAnchorProps) => {
  return (
    <TooltipWrapper id={id} role="tooltip" data-arrow-position={arrowPosition} data-testid="Tooltip">
      {text}
    </TooltipWrapper>
  );
};

export default TooltipAnchor;
